import useStyles from "./styles";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import { useTranslation } from "react-i18next";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTextSelect } from "../../../../components/CustomTextSelect/CustomTextSelect";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import { FC, useEffect } from "react";
import { useParams } from "react-router";
import {
  useCreateContratVehicle,
  useGetContratByVehicleId,
  useUpdateContratVehicle,
} from "../../../../api/contrat";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { useFormik } from "formik";
import {
  initialValuesVehicleContrat,
  validationSchemaCreateVehicleContrat,
} from "../../CreateVehicule/StepContract/validationSchemaContrat";
import {
  getPeriodicity,
  getPeriodicityByValue,
} from "../../../../utils/periodicity";
import dayjs from "dayjs";

type props = {
  opeModal: boolean;
  onClose: () => void;
  onCancel: () => void;
  /**
   * Vehicle to attach the contract to.
   *
   * Optional so the vehicle sheet keeps working unchanged - there the id is in
   * the route. It has to be overridable because the collaborator wizard also
   * opens this modal, and on /collaborator/create there is no :id param at
   * all, so reading useParams() alone would post to /vehicles/NaN.
   */
  vehicleId?: number;
};

export const ModalUpdateVehicleContrat: FC<props> = ({
  opeModal,
  onCancel,
  onClose,
  vehicleId: vehicleIdProp,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { id = "" } = useParams();
  const vehicleId = vehicleIdProp ?? parseInt(id);

  const { contrats } = useGetContratByVehicleId(getCurrentFleetId(), vehicleId);

  const { mutate: updateVehicle, isPending } = useUpdateContratVehicle(
    getCurrentFleetId(),
    vehicleId,
    contrats[0]?.id,
    onClose
  );

  const { mutate: createContratVehicle, isPending: isLoading } =
    useCreateContratVehicle(getCurrentFleetId(), vehicleId, onClose);

  useEffect(() => {
    if (contrats?.[0]?.id) {
      formik.setFieldValue("contractNumber", String(contrats[0].contract_number ?? ""));
      formik.setFieldValue("durationInMonths", String(contrats[0].duration ?? ""));
      formik.setFieldValue("kmOnContract", String(contrats[0].contract_km ?? ""));
      formik.setFieldValue(
        "numberOfWinterTires",
        String(contrats[0].number_winter_tire ?? "")
      );
      formik.setFieldValue(
        "numberOfSummerTires",
        String(contrats[0].number_summer_tire ?? "")
      );
      formik.setFieldValue("dateOfEntry", String(contrats[0].start_date ?? ""));
      formik.setFieldValue("releaseDate", String(contrats[0].end_date ?? ""));
      formik.setFieldValue("rentExcludingTax", String(contrats[0].rental_tax ?? ""));
      formik.setFieldValue("owner_company_id", String(contrats[0].owner_company_id ?? ""));
      formik.setFieldValue(
        "periodicity",
        getPeriodicityByValue(contrats[0].periodicity_id) ?? ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrats?.[0]?.id]);

  const formik = useFormik({
    initialValues: initialValuesVehicleContrat,
    validationSchema: validationSchemaCreateVehicleContrat,
    onSubmit: (values) => {
      const contrat = {
        contract_number: values.contractNumber ?? "",
        duration: Number(values.durationInMonths) || 0,
        start_date: values.dateOfEntry ?? "",
        end_date: dayjs(values.releaseDate).format("YYYY-MM-DD") ?? "",
        contract_km: Number(values.kmOnContract) || 0,
        rental_tax: Number(values.rentExcludingTax) || 0,
        periodicity_id: getPeriodicity(String(values.periodicity)) ?? 0,
        number_winter_tire: Number(values.numberOfWinterTires) || 0,
        number_summer_tire: Number(values.numberOfSummerTires) || 0,
        // BUGFIX: owner_company_id starts as "" in initialValuesVehicleContrat
        // and this modal has no field for it, so on the CREATE branch below it
        // was sent as 0. The backend gate ONLY_ADD_VEHICLES_FLEET_ADMIN then
        // looks up Company::where('id', 0), finds nothing, and throws 403
        // "ownerCompanyIsNotFound" - which meant adding a missing rent contract
        // from the vehicle sheet ALWAYS failed. A vehicle that reached
        // production without a contract therefore had no in-app recovery path,
        // and assigning it kept failing with "No contract attach to the
        // vehicle".
        //
        // Default to the current fleet, exactly as StepContract does. That is
        // the owner_company_id == companyId branch server-side (lessee_type
        // 'B'), i.e. "this fleet owns the vehicle" - the correct default, and it
        // satisfies the gate.
        owner_company_id:
          Number(values.owner_company_id) || getCurrentFleetId(),
      };
      if (contrats?.[0]?.id) {
        updateVehicle(contrat);
      } else {
        createContratVehicle(contrat);
      }
    },
  });

  useEffect(() => {
    if (formik?.values?.durationInMonths && formik?.values?.dateOfEntry) {
      const date = dayjs(formik?.values?.dateOfEntry)
        .add(Number(formik?.values?.durationInMonths), "month")
        .subtract(1, "day");

      formik?.setFieldValue("releaseDate", date.format("YYYY-MM-DD"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.durationInMonths, formik?.values?.dateOfEntry]);

  const handleSave = () => {
    formik.handleSubmit();
  };

  const arraysNumber: string[] = Array.from({ length: 10 }, (_, index) =>
    (index + 1).toString()
  );
  return (
    <CustomModal
      width={"663px"}
      open={opeModal}
      onCancel={onCancel}
      title={
        contrats?.[0]?.id
          ? t("vehicule.editContrat", "Edit contrat")
          : t("vehicule.addContrat", "Add a rent contract")
      }
      onClose={onClose}
      onSave={handleSave}
      loadingSave={isPending || isLoading}
    >
      <div className={classes.containerText}>
        <div className={classes.divModalText}>
          <CustomTextInput
            label={`${t("vehicule.contractNumber", "Contract number")} *`}
            placeholder={t("vehicule.contractNumber", "Contract number")}
            name="contractNumber"
            value={formik?.values?.contractNumber}
            onChange={formik.handleChange}
          />
          <CustomTextInput
            label={`${t("vehicule.durationInMonths", "Duration in months")} *`}
            placeholder={t("vehicule.durationInMonths", "Duration in months")}
            type="number"
            name="durationInMonths"
            value={formik?.values?.durationInMonths}
            onChange={formik.handleChange}
            marginBottom={"0px"}
          />
        </div>

        <div className={classes.divModalText}>
          <CustomTextDatePicker
            label={`${t("vehicule.dateOfEntry", "Date of entry")} *`}
            value={formik?.values?.dateOfEntry ? dayjs(formik.values.dateOfEntry) as any : null}
            onChange={(value) =>
              formik?.setFieldValue(
                "dateOfEntry",
                dayjs(value).format("YYYY-MM-DD")
              )
            }
          />
          <CustomTextDatePicker
            label={`${t("vehicule.releaseDate", "Release date")} *`}
            value={formik?.values?.releaseDate ? dayjs(formik.values.releaseDate) as any : null}
            onChange={(value) =>
              formik?.setFieldValue(
                "releaseDate",
                dayjs(value).format("YYYY-MM-DD")
              )
            }
          />
        </div>

        <CustomTextInput
          label={`${t("vehicule.kmOnContract", "Km on contract")} *`}
          placeholder={t("vehicule.kmOnContract", "Km on contract")}
          type="number"
          name="kmOnContract"
          value={formik?.values?.kmOnContract}
          onChange={formik.handleChange}
          marginBottom={"0px"}
        />
        <div className={classes.divModalText}>
          <CustomTextSelect
            label={t("vehicule.numberOfWinterTires", "Number of winter tires")}
            options={arraysNumber}
            placeholder={t(
              "vehicule.numberOfWinterTires",
              "Number of winter tires"
            )}
            name="numberOfWinterTires"
            value={formik.values.numberOfWinterTires}
            onChange={formik.handleChange}
          />
          <CustomTextSelect
            label={t("vehicule.numberOfSummerTires", "Number of summer tires")}
            options={arraysNumber}
            placeholder={t(
              "vehicule.numberOfSummerTires",
              "Number of summer tires"
            )}
            name="numberOfSummerTires"
            value={formik.values.numberOfSummerTires}
            onChange={formik.handleChange}
          />
        </div>
        <div className={classes.divModalText}>
          <CustomTextInput
            label={`${t("vehicule.rentExcludingTax", "Rent exluding tax")} *`}
            placeholder={t("vehicule.rentExcludingTax", "Rent exluding tax")}
            name="rentExcludingTax"
            type="number"
            value={formik?.values?.rentExcludingTax}
            onChange={formik.handleChange}
          />
          <CustomTextSelect
            label={`${t("common.periodicity", "Periodicity")} *`}
            options={["Journalier", "Mensuel", "Annuel"]}
            placeholder={t("vehicule.monthly", "Monthly")}
            name="periodicity"
            value={formik?.values?.periodicity}
            onChange={formik.handleChange}
          />
        </div>
      </div>
    </CustomModal>
  );
};
